import {
  ChangeEvent,
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useMemo,
  useState,
} from 'react'
import { SelectChangeEvent } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isEmpty, debounce } from 'lodash'
import { useUrlSearchParams } from 'use-url-search-params'
import { useFuzzySearch } from '@global/hooks'
import { getTemplatesQuery } from '../api'
import { Dataset, Folder, FolderOption, Query, SortBy } from '../types'
import { getFoldersFromDatasets, sortDatasets } from '../utils'
import { reducer } from './reducer'
import { ActionType, State, StaticFolders } from './types'

export const DEFAULT_SELECTED_FOLDER: FolderOption = {
  id: StaticFolders.all,
  value: '-1',
  text: 'All Folders',
}

export const initialState: State = {
  asc: true,
  search: '',
  selectedFolderId: DEFAULT_SELECTED_FOLDER.id,
  sortBy: SortBy.RECENT,
  filteredDatasets: null,
  selectedDataset: null,
}

interface ContextValues extends State {
  datasets: Dataset[] | null
  datasetsLoading: boolean
  folders: Folder[]
  foldersLoading: boolean
  searchResults: Dataset[] | null
  urlParams: any
  setStateFromUrlParams: (params: any) => void
  handleSearch: (e: ChangeEvent<HTMLInputElement> | { target: { value: string } }) => void
  clearSearch: () => void
  handleFilterFolder: (e: any) => void
  handleSortBy: (e: SelectChangeEvent<SortBy>) => void
  handleToggleAsc: () => void
  handleReset: () => void
  handleSelectDataset: (selected: Dataset | null) => void
  setShowDescriptions: (show: boolean) => void
  handleToggleFavorite: (datasetId: number, isFavorite: boolean) => void
  showDescriptions: boolean
}

const DatasetsContext = createContext<ContextValues | null>(null)

interface Props {
  children: ReactElement | ReactElement[]
}

export const DatasetsContextProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [showDescriptions, setShowDescriptions] = useState(false)
  const queryClient = useQueryClient()
  const { search, selectedFolderId, sortBy } = state

  const { data: filteredDatasets = [], isInitialLoading: datasetsLoading } = useQuery({
    ...getTemplatesQuery,
    refetchOnWindowFocus: false,
    select: useCallback(
      data => {
        if (String(selectedFolderId) === StaticFolders.all) {
          return sortDatasets(sortBy, data)
        }

        if (selectedFolderId === StaticFolders.favorites) {
          const favorites = sortDatasets(sortBy, data).filter(dataset => dataset?.favorite)
          return favorites
        }

        const filtered = sortDatasets(sortBy, data).filter(
          dataset => dataset?.folderId === Number(selectedFolderId),
        )

        return filtered
      },
      [sortBy, selectedFolderId],
    ),
  })

  /**
   * Get folders from datasets, used for the folder filter.
   * This is a separate query because we need to wait for the datasets to load first.
   * And we get them from the datasets because some users may have access to only a template,
   * but not the whole folder.
   */
  const { data: folders = [], isLoading: foldersLoading } = useQuery({
    queryKey: [Query.FOLDERS],
    queryFn: () => getFoldersFromDatasets(filteredDatasets),
    refetchOnWindowFocus: false,
    enabled: !datasetsLoading,
  })

  const [urlParams, setUrlParams] = useUrlSearchParams({})

  const [executeSearch, searchResults] = useFuzzySearch<Dataset>({
    collection: filteredDatasets || [],
    term: search,
    threshold: 0,
    options: {
      useExtendedSearch: false,
    },
    keys: ['name', 'description'],
    setTerm: (term: string) => {
      dispatch({ type: ActionType.setSearch, payload: term })
    },
  })

  // If URL Params are present, set the state from them as a default loading state.
  useEffect(() => {
    if (isEmpty(urlParams) || datasetsLoading || foldersLoading) return
    setStateFromUrlParams(urlParams)
  }, [datasetsLoading, foldersLoading])

  // If the URL params are empty, but the search is not, execute the search.
  // Search not being empty indicates the state is still showing the previous search results.
  useEffect(() => {
    if (!urlParams?.search && search) executeSearch('')
  }, [urlParams])

  const value = useMemo(
    () => ({
      ...state,
      datasets: filteredDatasets,
      datasetsLoading,
      folders,
      foldersLoading,
      searchResults,
      urlParams,
      setStateFromUrlParams,
      handleSearch: debounce(handleSearch, 300),
      clearSearch,
      handleFilterFolder,
      handleSortBy,
      handleToggleAsc,
      handleReset,
      handleSelectDataset,
      setShowDescriptions,
      handleToggleFavorite,
      showDescriptions,
    }),
    [
      state,
      filteredDatasets,
      datasetsLoading,
      folders,
      foldersLoading,
      searchResults,
      urlParams,
      setStateFromUrlParams,
      handleSearch,
      clearSearch,
      handleFilterFolder,
      handleSortBy,
      handleToggleAsc,
      handleReset,
      handleSelectDataset,
      setShowDescriptions,
      handleToggleFavorite,
      showDescriptions,
    ],
  )

  return <DatasetsContext.Provider value={value}>{children}</DatasetsContext.Provider>

  function setStateFromUrlParams(params: any): void {
    const searchFromUrl = params?.search?.toString()
    const folderIdFromUrl = params?.folder_id
    const sortByFromUrl = params?.sort_by?.toString()

    if (searchFromUrl) {
      dispatch({ type: ActionType.setSearch, payload: searchFromUrl })
      executeSearch(searchFromUrl)
    }

    if (folderIdFromUrl) {
      dispatch({ type: ActionType.setSelectedFolderId, payload: folderIdFromUrl })
    }

    if (sortByFromUrl) {
      dispatch({ type: ActionType.setSortBy, payload: sortByFromUrl })
    }
  }

  function handleSearch(e: ChangeEvent<HTMLInputElement> | { target: { value: string } }): void {
    const newValue = e.target.value
    executeSearch(newValue)
    setUrlParams({ search: newValue || undefined })
  }

  function clearSearch(): void {
    setUrlParams({ search: undefined })
    executeSearch('')
  }

  function handleFilterFolder(folderId: number): void {
    dispatch({ type: ActionType.setSelectedFolderId, payload: folderId })
    setUrlParams({
      folder_id: folderId === DEFAULT_SELECTED_FOLDER.id ? undefined : folderId,
    })
  }

  function handleSortBy(e: SelectChangeEvent<SortBy>): void {
    const newSortBy = e.target.value as SortBy
    dispatch({ type: ActionType.setSortBy, payload: newSortBy })
    setUrlParams({ sort_by: newSortBy === SortBy.RECENT ? undefined : newSortBy })
  }

  function handleToggleAsc(): void {
    dispatch({ type: ActionType.setAsc })
  }

  function handleReset(): void {
    dispatch({ type: ActionType.reset })
    setUrlParams({ search: undefined, folder_id: undefined, sort_by: undefined })
  }

  function handleSelectDataset(selected: Dataset | null): void {
    dispatch({ type: ActionType.setSelectedDataset, payload: selected })
  }

  function handleToggleFavorite(datasetId: number, isFavorite: boolean): void {
    const dataWithUpdatedFavorite = [...filteredDatasets]?.map(d => ({
      ...d,
      favorite: d.id === datasetId ? isFavorite : d.favorite,
    }))

    queryClient.setQueryData([Query.TEMPLATES], dataWithUpdatedFavorite)
  }
}

export const useDatasetsContext = () => {
  const context = useContext(DatasetsContext)

  if (!context) {
    throw new Error('useDatasetsContext must be used within a DatasetsContextProvider')
  }

  return context
}
